import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, GraduationCap, Users, Eye, Target, Flame, Lock, Book, ShieldCheck, TrendingUp, BarChart2, MessageCircle, BookOpen } from 'lucide-react';

const ecosystemFeatures = [
  { id: 'scholarships', title: 'Scholarship Hub', subtitle: 'Direct access to India\'s largest verified scholarship database. Auto-match based on your profile and apply securely.', icon: GraduationCap, color: '#9333ea', image: 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?q=80&w=800&auto=format&fit=crop' },
  { id: 'mentoring', title: 'Elite Mentoring', subtitle: 'Book 1-on-1 or group sessions with verified expert mentors across 50+ subjects.', icon: Users, color: '#2563eb', image: 'https://images.unsplash.com/photo-1515187029135-18ee286d815b?q=80&w=800&auto=format&fit=crop' },
  { id: 'peerconnect', title: 'Peer Connect', subtitle: 'Join subject-specific study groups, share doubts, and collaborate with peers across India.', icon: MessageCircle, color: '#db2777', image: 'https://images.unsplash.com/photo-1529156069898-49953e39b3ac?q=80&w=800&auto=format&fit=crop' },
  { id: 'library', title: 'Library & Tracker', subtitle: 'Premium notes, research papers, and a streak-based progress tracker to keep you consistent.', icon: BookOpen, color: '#10b981', image: 'https://images.unsplash.com/photo-1497633762265-9d179a990aa6?q=80&w=800&auto=format&fit=crop' },
];

const faqs = [
  { q: 'Is Ekalavya completely free?', a: 'Yes! Ekalavya is 100% free for all students from Class 6 through undergraduate. We believe knowledge should never be paywalled.' },
  { q: 'How do I find a scholarship?', a: 'Visit the Scholarship Hub from your dashboard or the navigation menu. Our AI-powered matching system helps surface the most relevant opportunities for your profile.' },
  { q: 'How do I become a mentor?', a: 'Register with a mentor account and complete your profile. Our team reviews all mentor applications to ensure quality. Once approved, you can list your sessions.' },
  { q: 'Is my data safe on Ekalavya?', a: 'Absolutely. We use Supabase with enterprise-grade security, encrypted sessions, and never sell user data to third parties.' },
  { q: 'Can I use Ekalavya on mobile?', a: 'Yes! Ekalavya is fully responsive and works great on all devices. A dedicated mobile app is in our roadmap.' },
  { q: 'How does the streak system work?', a: 'Your streak increments every day you actively engage with the platform — browsing scholarships, attending sessions, or contributing to the library counts.' },
];

const teamMembers = [
  { name: 'Arjun Verma', role: 'Co-Founder & CEO', img: 'https://i.pravatar.cc/200?u=arjun', badge: 'Visionary' },
  { name: 'Priya Sharma', role: 'Head of Education', img: 'https://i.pravatar.cc/200?u=priya', badge: 'Scholar' },
  { name: 'Rahul Nair', role: 'CTO', img: 'https://i.pravatar.cc/200?u=rahul', badge: 'Builder' },
  { name: 'Ananya Singh', role: 'Community Lead', img: 'https://i.pravatar.cc/200?u=ananya', badge: 'Connector' },
];

const coreValues = [
  { icon: Flame, title: 'Consistency', desc: 'Flash-in-the-pan motivation fades. We engineer systems that reward daily learning habits, ensuring genuine long-term success.', img: 'https://images.unsplash.com/photo-1434030216411-0b793f4b4173?q=80&w=800&auto=format&fit=crop', color: '#f59e0b', hover: 'rgba(245,158,11,0.55)' },
  { icon: Lock, title: 'Accessibility', desc: 'Knowledge should not be paywalled. Ekalavya is a 100% free platform, keeping doors open for every student.', img: 'https://images.unsplash.com/photo-1541339907198-e08756dedf3f?q=80&w=800&auto=format&fit=crop', color: '#3b82f6', hover: 'rgba(59,130,246,0.55)' },
  { icon: Users, title: 'Community', desc: 'Learning in isolation is slow. We build profound peer-to-peer networks where motivated minds elevate each other.', img: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?q=80&w=800&auto=format&fit=crop', color: '#ec4899', hover: 'rgba(236,72,153,0.55)' },
  { icon: Book, title: 'Skill Growth', desc: 'We move beyond pure academia. We train students in tangible skills crossing multiple domains of life.', img: 'https://images.unsplash.com/photo-1517048676732-d65bc937f952?q=80&w=800&auto=format&fit=crop', color: '#10b981', hover: 'rgba(16,185,129,0.55)' },
  { icon: ShieldCheck, title: 'Radical Trust', desc: 'Every mentor is verified. Every scholarship is legitimate. We provide a spam-free, ultra-secure sanctuary.', img: 'https://images.unsplash.com/photo-1582213782179-e0d53f98f2ca?q=80&w=800&auto=format&fit=crop', color: '#8b5cf6', hover: 'rgba(139,92,246,0.55)' },
];

const pathSteps = [
  { num: '01', title: 'Create Your Scholar Profile', desc: 'Sign up in seconds. Tell us about your academic level, goals, and skills to unlock personalized features.', icon: GraduationCap, color: '#9333ea', numColor: '#2563eb' },
  { num: '02', title: 'Explore & Get Matched', desc: 'Browse hand-curated scholarships, find expert mentors, and join communities that match your specific interests.', icon: Target, color: '#2563eb', numColor: '#9333ea' },
  { num: '03', title: 'Connect & Collaborate', desc: 'Book live mentoring sessions, join peer study groups, and access the academic library — all in one platform.', icon: Users, color: '#9333ea', numColor: '#2563eb' },
  { num: '04', title: 'Track & Celebrate Growth', desc: 'Build daily streaks, earn badges, and watch your growth score climb. Share your portfolio with the world.', icon: TrendingUp, color: '#2563eb', numColor: '#9333ea' },
];

/* ─────────────────────────────────────────────────────────────
   ECOSYSTEM SECTION  –  all hover/transition magic lives here
───────────────────────────────────────────────────────────── */
function EcosystemFeatureButton({ f, active, onClick, onMouseEnter }) {
  const [hov, setHov] = useState(false);
  return (
    <button
      onClick={onClick}
      onMouseEnter={() => { setHov(true); onMouseEnter && onMouseEnter(); }}
      onMouseLeave={() => setHov(false)}
      style={{
        display: 'flex', alignItems: 'center', gap: '1rem',
        padding: '1.25rem 1rem', borderRadius: '1rem',
        cursor: 'pointer', border: 'none', fontFamily: 'inherit',
        background: active
          ? `linear-gradient(135deg,${f.color}1c,${f.color}0e)`
          : hov ? 'rgba(147, 51, 234, 0.05)' : 'transparent',
        textAlign: 'left', width: '100%',
        borderLeft: active ? `3px solid ${f.color}` : '3px solid transparent',
        transform: hov && !active ? 'translateX(6px)' : 'translateX(0)',
        boxShadow: active ? `0 4px 20px -5px ${f.color}1a` : 'none',
        transition: 'all 0.25s cubic-bezier(0.25, 1, 0.5, 1)',
      }}
    >
      <div style={{
        width: '3rem', height: '3rem', borderRadius: '0.875rem',
        background: active ? `${f.color}22` : hov ? `${f.color}14` : `${f.color}0a`,
        border: `1px solid ${active ? f.color + '45' : hov ? f.color + '25' : f.color + '15'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
        transform: hov || active ? 'scale(1.1) rotate(-4deg)' : 'scale(1) rotate(0)',
        boxShadow: active ? `0 4px 16px ${f.color}28` : 'none',
        transition: 'all 0.3s cubic-bezier(0.25, 1, 0.5, 1)',
      }}>
        <f.icon size={22} color={f.color} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{
          color: active ? 'var(--navy-dark)' : hov ? 'var(--text-slate)' : 'var(--text-muted)',
          fontWeight: 700, fontSize: '1rem', marginBottom: '0.2rem',
          transition: 'color 0.2s',
        }}>{f.title}</p>
        <p style={{ color: active ? 'rgba(15, 23, 42, 0.65)' : 'rgba(15, 23, 42, 0.45)', fontSize: '0.78rem', lineHeight: 1.5 }}>{f.subtitle}</p>
      </div>
      {active && (
        <span style={{
          width: 6, height: 6, borderRadius: '50%', background: f.color, flexShrink: 0,
          boxShadow: `0 0 10px ${f.color}`,
          animation: 'ecoFeatPulse 1.4s ease-in-out infinite',
        }} />
      )}
    </button>
  );
}

function EcosystemPreview({ activeTab, onAuthClick }) {
  const [prevTab, setPrevTab] = useState(activeTab);
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    if (activeTab !== prevTab) {
      setVisible(false);
      const t = setTimeout(() => { setPrevTab(activeTab); setVisible(true); }, 180);
      return () => clearTimeout(t);
    }
  }, [activeTab, prevTab]);

  const barHeights = [45, 60, 40, 80, 55, 90, 100];

  const content = {
    scholarships: (
      <div style={{ background: '#ffffff', border: '1px solid rgba(192,132,252,0.15)', borderRadius: '1.5rem', padding: '1.75rem', boxShadow: '0 25px 50px -12px rgba(147, 51, 234, 0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.5rem' }}>
          <div style={{ width: '2.5rem', height: '2.5rem', background: 'rgba(192,132,252,0.15)', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(192,132,252,0.25)' }}>
            <GraduationCap size={18} color="#c084fc" />
          </div>
          <div>
            <p style={{ color: 'var(--navy-dark)', fontWeight: 700, fontSize: '1rem' }}>Match Generator</p>
            <p style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>AI-powered matching</p>
          </div>
        </div>
        {[
          { badge: '98% MATCH', badgeColor: '#c084fc', badgeBg: 'rgba(192,132,252,0.15)', icoColor: '#818cf8', icoW: '9rem' },
          { badge: 'SAVED', badgeColor: 'var(--text-muted)', badgeBg: 'rgba(0,0,0,0.05)', icoColor: '#34d399', icoW: '10rem' },
        ].map((item, i) => (
          <ScholarshipRow key={i} {...item} idx={i} />
        ))}
        <button onClick={onAuthClick} style={{ width: '100%', padding: '0.875rem', background: 'linear-gradient(135deg,var(--primary-gold),var(--primary-gold-deep))', color: 'white', borderRadius: '0.875rem', fontWeight: 700, fontSize: '0.9rem', cursor: 'pointer', border: 'none', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', transition: 'filter 0.2s, transform 0.2s' }}
          onMouseEnter={e => { e.currentTarget.style.filter = 'brightness(1.1)'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
          onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }}>
          Explore Scholarships <ArrowRight size={16} />
        </button>
      </div>
    ),
    mentoring: (
      <div style={{ background: '#ffffff', border: '1px solid rgba(59,130,246,0.15)', borderRadius: '1.5rem', padding: '1.75rem', boxShadow: '0 25px 50px -12px rgba(147, 51, 234, 0.08)' }}>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.65rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '1rem' }}>Live Sessions Today</p>
        {['Dr. Kavitha Rao — Physics', 'Prof. Anand Kumar — Mathematics', 'Ms. Deepa Iyer — Chemistry'].map((mentor, i) => (
          <MentorRow key={i} mentor={mentor} idx={i} onAuthClick={onAuthClick} />
        ))}
      </div>
    ),
    peerconnect: (
      <div style={{ background: '#ffffff', border: '1px solid rgba(168,85,247,0.15)', borderRadius: '1.5rem', padding: '1.75rem', boxShadow: '0 25px 50px -12px rgba(147, 51, 234, 0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div style={{ width: '2rem', height: '2rem', background: 'rgba(168,85,247,0.2)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Users size={14} color="#c084fc" />
            </div>
            <span style={{ color: 'var(--navy-dark)', fontWeight: 700 }}>JEE_Mains_2027</span>
          </div>
          <span style={{ color: 'var(--text-muted)', fontSize: '0.7rem', fontWeight: 700 }}>+24 members</span>
        </div>
        {[
          { user: 'Priya', msg: 'Can someone explain Rotational Mechanics Q42 from HC Verma?', me: false },
          { user: 'You', msg: 'Sure! The key is angular momentum conservation...', me: true },
        ].map((m, i) => (
          <div key={i} style={{ display: 'flex', gap: '0.75rem', marginBottom: '0.875rem', flexDirection: m.me ? 'row-reverse' : 'row' }}>
            <img src={`https://i.pravatar.cc/100?u=peer${i}`} style={{ width: '1.75rem', height: '1.75rem', borderRadius: '50%', flexShrink: 0 }} alt="" />
            <div style={{ background: m.me ? 'rgba(168,85,247,0.08)' : '#f3f4f6', border: `1px solid ${m.me ? 'rgba(168,85,247,0.15)' : 'rgba(0,0,0,0.05)'}`, borderRadius: '1rem', padding: '0.625rem 0.875rem', maxWidth: '80%' }}>
              <p style={{ color: m.me ? 'rgba(168,85,247,0.8)' : 'var(--text-muted)', fontSize: '0.6rem', fontWeight: 700, textTransform: 'uppercase', marginBottom: '0.2rem' }}>{m.user}</p>
              <p style={{ color: 'var(--text-slate)', fontSize: '0.8rem', lineHeight: 1.5 }}>{m.msg}</p>
            </div>
          </div>
        ))}
      </div>
    ),
    library: (
      <div style={{ background: '#ffffff', border: '1px solid rgba(16,185,129,0.15)', borderRadius: '1.5rem', padding: '1.75rem', boxShadow: '0 25px 50px -12px rgba(147, 51, 234, 0.08)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.25rem' }}>
          <p style={{ color: 'var(--navy-dark)', fontWeight: 700 }}>Performance Tracker</p>
          <BarChart2 size={18} color="#34d399" />
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', height: '7rem', marginBottom: '1.25rem' }}>
          {barHeights.map((h, i) => (
            <LibBar key={i} h={h} idx={i} />
          ))}
        </div>
        <div style={{ background: 'rgba(244,196,48,0.06)', border: '1px solid rgba(244,196,48,0.15)', borderRadius: '0.875rem', padding: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
          <div style={{ width: '2.5rem', height: '2.5rem', background: 'rgba(244,196,48,0.1)', borderRadius: '0.75rem', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(244,196,48,0.2)', flexShrink: 0 }}>
            <Flame size={20} color="#f4c430" />
          </div>
          <div>
            <p style={{ color: 'var(--navy-dark)', fontWeight: 700, fontSize: '0.95rem' }}>5 Day Streak 🔥</p>
            <p style={{ color: '#10b981', fontSize: '0.7rem', fontWeight: 700 }}>↑ Top 5% Learner this week</p>
          </div>
        </div>
      </div>
    ),
  };

  return (
    <div style={{
      opacity: visible ? 1 : 0,
      transform: visible ? 'translateY(0) scale(1)' : 'translateY(8px) scale(0.98)',
      transition: 'opacity 0.25s ease, transform 0.25s cubic-bezier(0.22,1,0.36,1)',
      width: '100%', maxWidth: '420px',
    }}>
      {content[prevTab]}
    </div>
  );
}

function ScholarshipRow({ badge, badgeColor, badgeBg, icoColor, icoW, idx }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '1rem', background: hov ? 'rgba(147,51,234,0.06)' : 'rgba(147,51,234,0.02)', borderRadius: '0.875rem', border: `1px solid ${hov ? 'rgba(192,132,252,0.25)' : 'rgba(147,51,234,0.08)'}`, marginBottom: '0.75rem', transform: hov ? 'translateX(4px)' : 'translateX(0)', transition: 'all 0.2s cubic-bezier(0.22,1,0.36,1)', cursor: 'pointer' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
        <div style={{ width: '2rem', height: '2rem', background: idx === 0 ? 'rgba(99,102,241,0.15)' : 'rgba(16,185,129,0.15)', borderRadius: '0.5rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <GraduationCap size={14} color={icoColor} />
        </div>
        <div>
          <div style={{ width: icoW, height: '8px', background: 'rgba(147,51,234,0.15)', borderRadius: '4px', marginBottom: '5px' }} />
          <div style={{ width: '6rem', height: '6px', background: 'rgba(147,51,234,0.08)', borderRadius: '4px' }} />
        </div>
      </div>
      <span style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.25rem 0.625rem', borderRadius: '9999px', background: badgeBg, color: badgeColor, whiteSpace: 'nowrap', letterSpacing: '0.05em' }}>{badge}</span>
    </div>
  );
}

function MentorRow({ mentor, idx, onAuthClick }) {
  const [hov, setHov] = useState(false);
  return (
    <div
      onMouseEnter={() => setHov(true)} onMouseLeave={() => setHov(false)}
      style={{ display: 'flex', alignItems: 'center', gap: '1rem', padding: '0.875rem', background: hov ? 'rgba(59,130,246,0.06)' : 'rgba(147,51,234,0.02)', borderRadius: '0.875rem', border: `1px solid ${hov ? 'rgba(59,130,246,0.25)' : 'rgba(147,51,234,0.08)'}`, marginBottom: '0.75rem', transform: hov ? 'translateX(4px)' : 'translateX(0)', transition: 'all 0.2s cubic-bezier(0.22,1,0.36,1)', cursor: 'pointer' }}>
      <img src={`https://i.pravatar.cc/80?u=mentor${idx + 10}`} style={{ width: '2.5rem', height: '2.5rem', borderRadius: '50%', border: `2px solid ${hov ? 'rgba(59,130,246,0.5)' : 'rgba(59,130,246,0.2)'}`, transition: 'border-color 0.2s', flexShrink: 0, transform: hov ? 'scale(1.06)' : 'scale(1)', transitionProperty: 'transform,border-color' }} alt="" />
      <div style={{ flex: 1 }}>
        <p style={{ color: 'var(--navy-dark)', fontWeight: 700, fontSize: '0.85rem' }}>{mentor}</p>
        <p style={{ color: 'var(--text-muted)', fontSize: '0.7rem' }}>5.0 ⭐ · {20 + idx * 15} students</p>
      </div>
      <button onClick={onAuthClick} style={{ fontSize: '0.6rem', fontWeight: 700, padding: '0.25rem 0.75rem', borderRadius: '9999px', background: hov ? 'rgba(59,130,246,0.2)' : 'rgba(59,130,246,0.1)', color: '#2563eb', border: `1px solid ${hov ? 'rgba(59,130,246,0.4)' : 'rgba(59,130,246,0.2)'}`, cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.2s', transform: hov ? 'scale(1.06)' : 'scale(1)' }}>Book</button>
    </div>
  );
}

function LibBar({ h, idx }) {
  const [mh, setMh] = useState(h);
  const isGold = idx === 6, isGreen = idx === 5;
  return (
    <div
      onMouseEnter={() => setMh(Math.min(h + 15, 100))}
      onMouseLeave={() => setMh(h)}
      style={{ flex: 1, borderRadius: '0.375rem 0.375rem 0 0', background: isGold ? 'rgba(192,132,252,0.5)' : isGreen ? 'rgba(16,185,129,0.4)' : 'rgba(0,0,0,0.06)', height: `${mh}%`, border: `1px solid ${isGold ? 'rgba(192,132,252,0.6)' : isGreen ? 'rgba(16,185,129,0.5)' : 'rgba(0,0,0,0.08)'}`, borderBottom: 'none', transition: 'height 0.4s cubic-bezier(0.22,1,0.36,1)', cursor: 'pointer' }}
    />
  );
}

function EcosystemSection({ onAuthClick, activeTab, setActiveTab }) {
  const activeFeature = ecosystemFeatures.find(f => f.id === activeTab) || ecosystemFeatures[0];

  return (
    <section
      id="ecosystem"
      style={{
        paddingTop: '6rem',
        paddingBottom: '6rem',
        position: 'relative',
        backgroundColor: '#ffffff',
        borderTop: '1px solid rgba(147, 51, 234, 0.06)',
        borderBottom: '1px solid rgba(147, 51, 234, 0.06)',
        overflow: 'hidden'
      }}
    >
      <style>{`
        @keyframes ecoFeatPulse { 0%,100%{opacity:1;transform:scale(1)} 50%{opacity:0.5;transform:scale(0.85)} }
        @keyframes ecoTabIn { from{opacity:0;transform:translateY(6px)} to{opacity:1;transform:translateY(0)} }
      `}</style>

      {/* Subtle education ecosystem background image (at very low opacity to remain clean and white) */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'url("https://images.unsplash.com/photo-1523240795612-9a054b0db644?q=80&w=1200&auto=format&fit=crop")',
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        opacity: 0.035,
        filter: 'grayscale(0.5) contrast(1.1)',
        pointerEvents: 'none',
      }} />

      {/* Dotted grid pattern overlay */}
      <div style={{
        position: 'absolute',
        inset: 0,
        backgroundImage: 'radial-gradient(rgba(147, 51, 234, 0.04) 1.5px, transparent 1.5px), linear-gradient(180deg, rgba(255,255,255,0.95) 0%, rgba(250,248,255,0.95) 50%, rgba(246,241,255,0.95) 100%)',
        backgroundSize: '24px 24px, 100% 100%',
        pointerEvents: 'none',
      }} />

      {/* Ambient glows that follow active tab color */}
      <div style={{ position: 'absolute', left: '10%', top: '10%', width: '400px', height: '400px', background: `radial-gradient(circle, ${activeFeature.color}08 0%, transparent 70%)`, filter: 'blur(50px)', pointerEvents: 'none', transition: 'background 0.6s ease' }} />
      <div style={{ position: 'absolute', right: '10%', bottom: '10%', width: '500px', height: '500px', background: `radial-gradient(circle, ${activeFeature.color}06 0%, transparent 70%)`, filter: 'blur(60px)', pointerEvents: 'none', transition: 'background 0.6s ease' }} />

      <div className="container" style={{ position: 'relative', zIndex: 10 }}>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <div className="section-label-pill">
            <span className="section-label-dot" />
            <span className="section-label-text">Our Ecosystem</span>
          </div>
          <h2 style={{ fontSize: 'clamp(2.5rem, 5vw, 4rem)', fontWeight: 800, lineHeight: 1.15, color: 'var(--navy-dark)', letterSpacing: '-0.03em' }}>
            Everything you need.<br />
            <span className="text-gold-gradient">In one place.</span>
          </h2>
          <p style={{ marginTop: '1.5rem', color: 'var(--text-slate)', fontSize: '1.1rem', lineHeight: 1.7, maxWidth: '600px', margin: '1.5rem auto 0' }}>
            Stop hunting across a dozen websites. Ekalavya brings scholarships, mentors, peers, notes, and progress tracking under one premium roof.
          </p>
        </div>

        {/* Main split panel */}
        <div className="ecosystem-split-panel" style={{
          background: '#ffffff', border: `1px solid rgba(147, 51, 234, 0.1)`,
          borderRadius: '2rem', overflow: 'hidden',
          boxShadow: `0 30px 70px rgba(147, 51, 234, 0.06), 0 0 0 1px rgba(147, 51, 234, 0.03)`,
          transition: 'border-color 0.5s ease, box-shadow 0.5s ease',
        }}>
          {/* Left list */}
          <div style={{ borderRight: `1px solid rgba(147, 51, 234, 0.08)`, display: 'flex', flexDirection: 'column', padding: '1.25rem 1rem', gap: '0.5rem', background: '#fafafb', transition: 'border-color 0.5s' }}>
            {ecosystemFeatures.map(f => (
              <EcosystemFeatureButton key={f.id} f={f} active={activeTab === f.id} onClick={() => setActiveTab(f.id)} onMouseEnter={() => setActiveTab(f.id)} />
            ))}
          </div>
          {/* Right preview */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2.5rem', background: '#fafafb', position: 'relative', overflow: 'hidden' }}>
            {/* Dynamic feature background images with transition */}
            {ecosystemFeatures.map(f => (
              <div
                key={f.id}
                style={{
                  position: 'absolute',
                  inset: 0,
                  backgroundImage: `url(${f.image})`,
                  backgroundSize: 'cover',
                  backgroundPosition: 'center',
                  opacity: activeTab === f.id ? 0.08 : 0,
                  transition: 'opacity 0.5s cubic-bezier(0.25, 1, 0.5, 1)',
                  filter: 'grayscale(0.1) contrast(1.05)',
                  pointerEvents: 'none',
                }}
              />
            ))}
            {/* Subtle color wash overlay */}
            <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse at 60% 40%, ${activeFeature.color}05, transparent 75%)`, transition: 'background 0.5s ease', pointerEvents: 'none' }} />
            <EcosystemPreview activeTab={activeTab} onAuthClick={onAuthClick} />
          </div>
        </div>
      </div>
    </section>
  );
}

function CoreValueCard({ icon: Icon, title, desc, img, color, hover }) {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <div
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{
        flex: isHovered ? '2.5' : '1',
        borderRadius: '2rem',
        overflow: 'hidden',
        position: 'relative',
        cursor: 'pointer',
        transition: 'all 0.5s cubic-bezier(0.25, 1, 0.5, 1)',
        border: `1px solid ${isHovered ? color : 'rgba(255, 255, 255, 0.1)'}`,
        boxShadow: isHovered
          ? `0 20px 50px -10px ${color}66, 0 0 20px 2px ${color}33, inset 0 0 24px ${color}15`
          : 'none',
        transform: isHovered ? 'translateY(-6px)' : 'translateY(0)',
      }}
    >
      <img
        src={img}
        style={{
          position: 'absolute',
          inset: 0,
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          opacity: isHovered ? 0.75 : 0.45,
          filter: isHovered ? 'grayscale(0) brightness(1.05)' : 'grayscale(0.6) brightness(0.8)',
          transition: 'all 0.6s cubic-bezier(0.25, 1, 0.5, 1)',
          transform: isHovered ? 'scale(1.08)' : 'scale(1)',
        }}
        alt={title}
      />
      <div style={{ position: 'absolute', inset: 0, background: 'rgba(10, 14, 23, 0.5)', zIndex: 1 }} />
      <div style={{ position: 'absolute', inset: 0, background: `linear-gradient(to top, ${isHovered ? hover : 'rgba(147, 51, 234, 0.1)'} 0%, transparent 70%)`, zIndex: 2, transition: 'background 0.5s' }} />

      <div style={{ position: 'absolute', bottom: '2rem', left: '2rem', right: '2rem', zIndex: 10 }}>
        {/* Icon with glow halo */}
        <div style={{ position: 'relative', width: '3.5rem', height: '3.5rem', marginBottom: '1rem' }}>
          {/* Pulsating glow ring behind icon */}
          <div style={{
            position: 'absolute', inset: '-8px', borderRadius: '1.25rem',
            background: `radial-gradient(circle, ${color}${isHovered ? '55' : '00'} 0%, transparent 70%)`,
            animation: isHovered ? 'iconGlowPulse 2s ease-in-out infinite' : 'none',
            transition: 'background 0.4s',
            pointerEvents: 'none',
          }} />
          <div style={{
            width: '3.5rem',
            height: '3.5rem',
            background: isHovered ? `${color}33` : 'rgba(255,255,255,0.08)',
            backdropFilter: 'blur(10px)',
            borderRadius: '1rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            border: `1px solid ${isHovered ? color : 'rgba(255,255,255,0.15)'}`,
            transform: isHovered ? 'scale(1.1) rotate(-4deg)' : 'scale(1) rotate(0)',
            boxShadow: isHovered ? `0 0 20px ${color}66, 0 0 40px ${color}33, 0 0 60px ${color}18` : 'none',
            transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)',
          }}>
            <Icon size={24} color={isHovered ? '#ffffff' : color} style={{
              transition: 'color 0.3s, filter 0.4s',
              filter: isHovered ? `drop-shadow(0 0 8px ${color})` : 'none',
            }} />
          </div>
        </div>
        <h3 style={{
          color: 'white',
          fontWeight: 900,
          fontSize: '1.4rem',
          textShadow: '0 2px 10px rgba(0,0,0,0.6)',
          letterSpacing: '-0.01em'
        }}>{title}</h3>
        <p style={{
          color: isHovered ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.65)',
          fontSize: '0.88rem',
          lineHeight: 1.7,
          marginTop: '0.6rem',
          display: '-webkit-box',
          WebkitLineClamp: isHovered ? 4 : 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden',
          transition: 'color 0.3s, WebkitLineClamp 0.3s',
        }}>{desc}</p>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────────────────────
   PATH TIMELINE — Scroll-progress vertical line + active step glow system
   Mimics the CloudNestor "How It Works" scroll-driven glow behavior:
   • Vertical line fills from top as you scroll DOWN into the section
   • Cards glow when the line "reaches" them
   • Cards dim again when you scroll back UP past them
─────────────────────────────────────────────────────────────────────────── */
function PathTimeline({ pathSteps }) {
  const containerRef = useRef(null);
  const [lineProgress, setLineProgress] = useState(0); // 0 → 1 (filled fraction of line)

  useEffect(() => {
    const onScroll = () => {
      const el = containerRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const totalH = el.offsetHeight;
      const windowH = window.innerHeight;

      // progress: 0 when top of container is at bottom of viewport
      //           1 when bottom of container is at center of viewport
      const scrolled = windowH - rect.top;
      const range = totalH + windowH * 0.5;
      const p = Math.max(0, Math.min(1, scrolled / range));
      setLineProgress(p);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll(); // run once on mount
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Each step activates when the line progress passes its threshold
  // With 4 steps evenly distributed: 0.15, 0.38, 0.61, 0.84
  const stepThresholds = pathSteps.map((_, i) =>
    0.15 + (i * 0.69) / (pathSteps.length - 1 || 1)
  );

  return (
    <div className="path-timeline-container" ref={containerRef}>
      {/* Background track (static, dim) */}
      <div className="path-timeline-line-bg" style={{
        position: 'absolute', left: '50%', top: 0, bottom: 0,
        width: '2px', transform: 'translateX(-50%)',
        background: 'rgba(147,51,234,0.12)',
        borderRadius: '2px',
        zIndex: 0,
      }} />

      {/* Filled progress line (grows downward, with glowing tip) */}
      <div className="path-timeline-line-fill" style={{
        position: 'absolute', left: '50%', top: 0,
        width: '2px', transform: 'translateX(-50%)',
        height: `${lineProgress * 100}%`,
        background: 'linear-gradient(to bottom, #9333ea 0%, #3b82f6 60%, #9333ea 100%)',
        borderRadius: '2px',
        transition: 'height 0.08s linear',
        zIndex: 1,
      }}>
        {/* Glowing orb at the tip */}
        {lineProgress > 0 && lineProgress < 1 && (
          <div style={{
            position: 'absolute', bottom: '-7px', left: '50%',
            transform: 'translateX(-50%)',
            width: '14px', height: '14px', borderRadius: '50%',
            background: 'radial-gradient(circle, #9333ea 0%, #7c3aed 60%, transparent 100%)',
            boxShadow: '0 0 12px 4px rgba(147,51,234,0.55)',
            animation: 'cnLineTip 1.2s ease-in-out infinite',
          }} />
        )}
      </div>

      <style>{`
        @keyframes cnLineTip {
          0%,100% { opacity: 0.8; box-shadow: 0 0 10px 3px rgba(147,51,234,0.5); }
          50%      { opacity: 1;   box-shadow: 0 0 18px 7px rgba(147,51,234,0.8); }
        }
      `}</style>

      {/* Steps */}
      <div className="path-timeline-steps">
        {pathSteps.map((step, idx) => (
          <TimelineStep
            key={step.num}
            {...step}
            idx={idx}
            active={lineProgress >= stepThresholds[idx]}
          />
        ))}
      </div>
    </div>
  );
}

/* Scroll-progress-driven timeline step — pixel-perfect CloudNestor clone */
function TimelineStep({ num, title, desc, icon: Icon, color, numColor, idx, active }) {
  const ref = useRef(null);
  const [entered, setEntered] = useState(false);
  const isLeft = idx % 2 === 0;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setEntered(true); obs.unobserve(el); } },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const card = (
    <div className="path-timeline-card" style={{
      border: `1.5px solid ${active ? color + 'aa' : 'rgba(209,213,235,0.8)'}`,
      opacity: entered ? 1 : 0,
      transform: entered
        ? 'translateX(0) scale(1)'
        : `translateX(${isLeft ? '-50px' : '50px'}) scale(0.96)`,
      boxShadow: active
        ? `0 4px 6px ${color}15, 0 12px 40px ${color}20, 0 0 0 1px ${color}18`
        : '0 2px 12px rgba(147,51,234,0.04)',
      background: active
        ? `linear-gradient(145deg, #ffffff 60%, ${color}08 100%)`
        : '#ffffff',
      '--step-color': color,
      '--step-color-rgb': color === '#9333ea' ? '147,51,234' : '37,99,235',
    }}>
      <h4 style={{
        color: active ? color : '#1e293b',
        fontWeight: 800,
        fontSize: '1.25rem',
        marginBottom: '0.75rem',
        lineHeight: 1.35,
        transition: 'color 0.4s ease',
      }}>{title}</h4>
      <p style={{
        color: '#64748b',
        fontSize: '0.95rem',
        lineHeight: 1.8,
        margin: 0,
      }}>{desc}</p>
    </div>
  );

  return (
    <div className="path-timeline-step" ref={ref}>
      {/* Left panel */}
      <div className="path-timeline-panel left">
        {isLeft ? card : null}
      </div>

      {/* Center icon badge */}
      <div className="path-timeline-badge-wrapper" style={{ position: 'relative', flexShrink: 0, zIndex: 2 }}>
        {/* Glow ring (active only) */}
        {active && (
          <div style={{
            position: 'absolute', inset: '-12px', borderRadius: '1.25rem',
            background: `radial-gradient(circle, ${color}28 0%, transparent 70%)`,
            animation: 'cnIconPulse 2.2s ease-in-out infinite',
            pointerEvents: 'none',
          }} />
        )}
        {/* Icon box — 64px, matching CloudNestor */}
        <div style={{
          width: '4rem',              /* 64px — CloudNestor size */
          height: '4rem',
          borderRadius: '1rem',       /* rounded-2xl */
          background: active
            ? `linear-gradient(135deg, ${color} 0%, ${color}bb 100%)`
            : 'rgba(203,213,225,0.5)', /* faded gray when inactive */
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: active
            ? `0 0 0 4px ${color}20, 0 8px 24px ${color}50`
            : '0 2px 8px rgba(0,0,0,0.06)',
          transform: entered
            ? (active ? 'scale(1.06)' : 'scale(1)')
            : 'scale(0.5)',
          opacity: entered ? 1 : 0,
          transition: `
            transform 0.55s cubic-bezier(0.34,1.56,0.64,1) ${idx * 0.08 + 0.1}s,
            opacity 0.5s ease ${idx * 0.08 + 0.1}s,
            background 0.4s ease,
            box-shadow 0.4s ease
          `,
        }}>
          <Icon size={22} color={active ? '#ffffff' : '#94a3b8'} />
        </div>

        {/* Step number badge */}
        <div style={{
          position: 'absolute', top: '-8px', right: '-9px',
          width: '1.65rem', height: '1.65rem', borderRadius: '50%',
          background: active ? numColor : '#cbd5e1', /* faded gray inactive, vivid active */
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '0.68rem', fontWeight: 900, color: '#ffffff',
          border: '2.5px solid #ffffff',
          boxShadow: active ? `0 2px 10px ${numColor}80` : '0 1px 4px rgba(0,0,0,0.1)',
          transform: entered ? 'scale(1)' : 'scale(0)',
          opacity: entered ? 1 : 0,
          transition: `
            transform 0.45s cubic-bezier(0.34,1.56,0.64,1) ${idx * 0.08 + 0.25}s,
            opacity 0.4s ease ${idx * 0.08 + 0.25}s,
            background 0.4s ease,
            box-shadow 0.4s ease
          `,
          zIndex: 3,
        }}>{parseInt(num)}</div>
      </div>

      {/* Right panel */}
      <div className="path-timeline-panel right">
        {!isLeft ? card : null}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────
   FAQ ITEM — CloudNestor-Inspired Numbered Accordion
───────────────────────────────────────────────────────── */
function FaqItem({ idx, q, a, open, onToggle }) {
  const [hov, setHov] = useState(false);
  const num = String(idx + 1).padStart(2, '0');

  return (
    <div
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        borderBottom: `1px solid ${open ? 'rgba(147,51,234,0.18)' : 'rgba(147,51,234,0.08)'}`,
        transition: 'border-color 0.25s',
        paddingBottom: '0.25rem',
      }}
    >
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center',
          gap: '1.25rem', padding: '1.5rem 0',
          background: 'transparent', border: 'none', cursor: 'pointer',
          textAlign: 'left', fontFamily: 'inherit',
        }}
      >
        {/* Numbered circle */}
        <div style={{
          width: '2.5rem', height: '2.5rem', borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: open
            ? 'linear-gradient(135deg, #9333ea, #a855f7)'
            : hov ? 'rgba(147,51,234,0.1)' : 'rgba(147,51,234,0.06)',
          border: `1.5px solid ${open ? '#9333ea' : hov ? 'rgba(147,51,234,0.3)' : 'rgba(147,51,234,0.12)'}`,
          transition: 'all 0.3s cubic-bezier(0.25,1,0.5,1)',
          boxShadow: open ? '0 4px 14px rgba(147,51,234,0.25)' : 'none',
        }}>
          <span style={{
            fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.02em',
            color: open ? '#ffffff' : hov ? '#9333ea' : '#a78bfa',
            transition: 'color 0.3s',
          }}>{num}</span>
        </div>

        {/* Question text */}
        <span style={{
          flex: 1, fontSize: '1.05rem', fontWeight: 700, lineHeight: 1.5,
          color: open ? '#9333ea' : hov ? 'var(--navy-dark)' : 'var(--navy-dark)',
          transition: 'color 0.25s',
        }}>{q}</span>

        {/* +/- icon */}
        <div style={{
          width: '2rem', height: '2rem', borderRadius: '50%', flexShrink: 0,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          background: open ? '#9333ea' : hov ? 'rgba(147,51,234,0.1)' : 'rgba(147,51,234,0.06)',
          border: `1px solid ${open ? '#9333ea' : 'rgba(147,51,234,0.12)'}`,
          transition: 'all 0.3s cubic-bezier(0.25,1,0.5,1)',
          transform: open ? 'rotate(45deg)' : 'rotate(0)',
        }}>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M7 1v12M1 7h12" stroke={open ? '#fff' : '#9333ea'} strokeWidth="2" strokeLinecap="round" />
          </svg>
        </div>
      </button>

      {/* Animated answer */}
      <div style={{
        overflow: 'hidden',
        maxHeight: open ? '300px' : '0',
        opacity: open ? 1 : 0,
        transition: 'max-height 0.4s cubic-bezier(0.25,1,0.5,1), opacity 0.3s ease',
      }}>
        <p style={{
          color: 'var(--text-slate)', fontSize: '0.97rem', lineHeight: 1.8,
          paddingLeft: '3.75rem', paddingBottom: '1.5rem', margin: 0,
        }}>{a}</p>
      </div>
    </div>
  );
}

export default function Home({ onAuthClick }) {
  const [openFaq, setOpenFaq] = useState(null);
  const [activeTab, setActiveTab] = useState('scholarships');
  const [heroHover, setHeroHover] = useState(false);

  return (
    <div>
      {/* Hero */}
      <section className="hero-section container" id="home" style={{ position: 'relative', overflow: 'hidden' }}>
        <div className="hero-mesh-gradient" />
        <div className="hero-grid reveal">
          <div className="hero-text-col">
            <div>
              <h1 className="hero-title" style={{ marginBottom: '1.5rem' }}>
                Empowering Every<br />
                <span className="text-gold-gradient text-glow-gold">Student. Everywhere.</span>
              </h1>
              <p className="hero-subtitle" style={{ marginBottom: '2rem', maxWidth: '540px' }}>
                Ekalavya is the all-in-one ecosystem for Class 6–12 and undergraduate students across India. Access exclusive scholarships, connect with expert mentors, and master your subjects with peer-led study sessions and premium notes — all designed to help you stay consistent and confident.
              </p>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.25rem' }}>
              <Link to="/scholarships" className="cta-primary">
                Get Started Free <ArrowRight size={20} />
              </Link>
              <button onClick={onAuthClick} className="cta-outline">Join Ekalavya</button>
            </div>
          </div>
          <div className="hero-img-col"
            onMouseEnter={() => setHeroHover(true)}
            onMouseLeave={() => setHeroHover(false)}
          >
            <div style={{ transform: heroHover ? 'translateY(-12px) scale(1.02)' : 'translateY(0) scale(1)', transition: 'transform 0.4s cubic-bezier(0.25, 1, 0.5, 1)', width: '100%' }}>
              <div className="glass-card" style={{
                padding: '0.625rem',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '2rem',
                zIndex: 1,
                boxShadow: heroHover
                  ? '0 35px 80px -15px rgba(147, 51, 234, 0.35), 0 0 0 1px rgba(147, 51, 234, 0.25)'
                  : '0 25px 60px -15px rgba(147, 51, 234, 0.22), 0 0 0 1px rgba(147, 51, 234, 0.15)',
                background: heroHover ? 'rgba(255, 255, 255, 0.75)' : 'rgba(255, 255, 255, 0.5)',
                transition: 'all 0.4s cubic-bezier(0.25, 1, 0.5, 1)'
              }}>
                <div style={{ position: 'absolute', inset: 0, opacity: heroHover ? 0.08 : 0.03, background: 'linear-gradient(135deg,var(--primary-gold),var(--primary-gold-deep))', transition: 'opacity 0.4s', borderRadius: 'inherit' }} />
                <img
                  src="/hero-bg.png"
                  alt="Student using Ekalavya"
                  style={{
                    borderRadius: '1.5rem',
                    width: '100%',
                    height: 'auto',
                    objectFit: 'cover',
                    aspectRatio: '4/3',
                    display: 'block',
                    boxShadow: '0 8px 30px rgba(0,0,0,0.15)',
                    filter: heroHover ? 'grayscale(0) brightness(1) contrast(1)' : 'grayscale(0.75) brightness(0.9) contrast(1.05)',
                    transition: 'filter 0.5s ease-out'
                  }}
                />
              </div>
            </div>
            <div style={{ position: 'absolute', top: '-40px', right: '-40px', width: '200px', height: '200px', background: 'rgba(192,132,252,0.1)', filter: 'blur(60px)', borderRadius: '50%', pointerEvents: 'none', zIndex: -1 }} />
          </div>
        </div>
      </section>



      {/* ── ECOSYSTEM SECTION — enhanced with smooth transitions & hover effects ── */}
      <EcosystemSection onAuthClick={onAuthClick} activeTab={activeTab} setActiveTab={setActiveTab} />

      {/* About / Vision & Mission */}
      <section id="about" style={{ paddingTop: '5rem', paddingBottom: '5rem', position: 'relative', overflow: 'hidden', background: 'linear-gradient(160deg, #ffffff 0%, #faf8ff 100%)' }}>
        <div style={{ position: 'absolute', top: 0, right: 0, width: '800px', height: '800px', background: 'rgba(147,51,234,0.04)', filter: 'blur(120px)', borderRadius: '50%', transform: 'translate(33%, -33%)', pointerEvents: 'none' }} />
        <div className="container" style={{ position: 'relative', zIndex: 10 }}>
          <div className="purpose-grid">
            <div>
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '9999px', background: 'rgba(147,51,234,0.05)', border: '1px solid rgba(147,51,234,0.15)', marginBottom: '2rem' }}>
                <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent-amber)' }} />
                <span style={{ color: 'var(--primary-gold)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em' }}>Our Purpose</span>
              </div>
              <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', fontWeight: 900, color: 'var(--navy-dark)', lineHeight: 1.1, marginBottom: '2rem', letterSpacing: '-0.02em' }}>
                We're building an <br />
                <span className="text-gold-gradient">equal playing field</span><br />
                <span style={{ color: 'rgba(15, 23, 42, 0.4)' }}>for every student.</span>
              </h2>
              <p style={{ color: 'var(--text-slate)', fontSize: '1.1rem', lineHeight: 1.8 }}>
                Talent is universal, but opportunity is not. Ekalavya exists to bridge the gap between ambition and resources by providing a world-class digital ecosystem, absolutely free.
              </p>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
              {[{ icon: Eye, title: 'Our Vision', desc: 'A world where every student — regardless of location or background — has equal access to elite mentorship and the unwavering community support needed to excel.' }, { icon: Target, title: 'Our Mission', desc: 'To engineer a free, high-performance educational platform that democratizes opportunities and instills a powerful sense of independent learning in millions.' }].map(({ icon: Icon, title, desc }) => (
                <div key={title} className="glass-card" style={{ padding: '1.75rem', position: 'relative', overflow: 'hidden', transition: 'all 0.3s', background: '#ffffff', border: '1px solid rgba(147,51,234,0.12)', boxShadow: '0 4px 20px rgba(147,51,234,0.04)' }}>
                  <div style={{ width: '4rem', height: '4rem', background: 'rgba(147,51,234,0.06)', borderRadius: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '1.25rem', border: '1px solid rgba(147,51,234,0.12)' }}>
                    <Icon size={28} color="var(--primary-gold)" />
                  </div>
                  <h3 style={{ fontSize: '1.5rem', fontWeight: 900, color: 'var(--navy-dark)', marginBottom: '1rem', letterSpacing: '-0.02em' }}>{title}</h3>
                  <p style={{ color: 'var(--text-slate)', lineHeight: 1.8 }}>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section style={{ paddingTop: '5rem', paddingBottom: '5rem', background: 'linear-gradient(160deg, #ffffff 0%, #fcf9ff 100%)', position: 'relative', overflow: 'hidden' }}>
        <div className="container" style={{ position: 'relative', zIndex: 10 }}>
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', padding: '0.5rem 1rem', borderRadius: '9999px', background: 'rgba(147,51,234,0.05)', border: '1px solid rgba(147,51,234,0.12)', marginBottom: '1.5rem' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: 'var(--accent-amber)' }} />
              <span style={{ color: 'var(--primary-gold)', fontSize: '0.75rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.2em' }}>Our DNA</span>
            </div>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', fontWeight: 900, color: 'var(--navy-dark)', marginBottom: '1rem' }}>
              What <span className="text-gold-gradient">Drives Us</span>
            </h2>
            <p style={{ color: 'var(--text-slate)', fontSize: '1.1rem' }}>The non-negotiable principles that shape every decision we make.</p>
          </div>
          <div className="core-values-container">
            {coreValues.map((val) => (
              <CoreValueCard key={val.title} {...val} />
            ))}
          </div>
        </div>
      </section>

      {/* Path / How it Works — Exact CloudNestor Style */}
      <section id="path" style={{ paddingTop: '6rem', paddingBottom: '6rem', background: '#ffffff', position: 'relative', overflow: 'hidden' }}>
        {/* CloudNestor: subtle grid texture */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'linear-gradient(rgba(147,51,234,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(147,51,234,0.04) 1px, transparent 1px)',
          backgroundSize: '40px 40px',
          opacity: 0.6,
        }} />

        <style>{`
          @keyframes cnIconPulse { 0%,100%{opacity:0.5;transform:scale(1)} 50%{opacity:1;transform:scale(1.15)} }
          @keyframes floatChar { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-14px)} }
        `}</style>

        <div className="container" style={{ position: 'relative', zIndex: 2 }}>
          {/* Section header — centered like CloudNestor */}
          <div style={{ textAlign: 'center', marginBottom: '4.5rem' }}>
            <span style={{
              display: 'inline-block', fontSize: '0.72rem', fontWeight: 800,
              letterSpacing: '0.18em', textTransform: 'uppercase',
              color: '#7c3aed', background: 'rgba(124,58,237,0.08)',
              border: '1px solid rgba(124,58,237,0.18)',
              padding: '0.375rem 1rem', borderRadius: '9999px', marginBottom: '1.25rem',
            }}>Get started in minutes</span>
            <h2 style={{
              fontSize: 'clamp(2.4rem, 5vw, 3.75rem)', fontWeight: 900,
              color: 'var(--navy-dark)', lineHeight: 1.1,
              letterSpacing: '-0.03em', marginBottom: '0.5rem',
            }}>
              Your path to <span className="text-gold-gradient">excellence</span>
            </h2>
            <svg width="120" height="12" viewBox="0 0 120 12" fill="none" style={{ margin: '0 auto 1.25rem', display: 'block' }}>
              <path d="M2 8 C12 2, 22 14, 32 8 S52 2, 62 8 S82 14, 92 8 S112 2, 118 8" stroke="#9333ea" strokeWidth="2.5" strokeLinecap="round" fill="none" />
            </svg>
            <p style={{ color: 'var(--text-slate)', fontSize: '1.05rem', lineHeight: 1.8, maxWidth: '520px', margin: '0 auto' }}>
              Four simple steps. Thousands of possibilities. Your journey to becoming the scholar you were born to be.
            </p>
          </div>

          {/* Two-column: character left, timeline right */}
          <div className="path-section-grid">

            {/* Left: CloudNestor-style stacked 3D blocks with floating character */}
            <div className="path-left-col">
              {/* Floating character */}
              <div style={{
                position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)',
                animation: 'floatChar 5s ease-in-out infinite', zIndex: 3,
              }}>
                <div style={{
                  width: '52px', height: '52px', borderRadius: '50%', margin: '0 auto 2px',
                  background: 'linear-gradient(135deg, #a78bfa, #7c3aed)',
                  border: '3px solid rgba(255,255,255,0.35)',
                  boxShadow: '0 4px 16px rgba(147,51,234,0.4)',
                }} />
                <div style={{
                  width: '80px', height: '90px', margin: '0 auto',
                  background: 'linear-gradient(160deg, #7c3aed 0%, #9333ea 100%)',
                  borderRadius: '32px 32px 18px 18px',
                  boxShadow: '0 8px 24px rgba(147,51,234,0.35)',
                  border: '2px solid rgba(255,255,255,0.15)',
                }} />
              </div>

              {/* Stacked 3D blocks */}
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px', width: '100%', zIndex: 2 }}>
                {pathSteps.slice().reverse().map((s, i) => (
                  <div key={s.num} style={{
                    width: `${100 + i * 26}px`,
                    height: '78px',
                    background: `linear-gradient(145deg, ${s.color} 0%, ${s.color}cc 100%)`,
                    borderRadius: '1rem',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', justifyContent: 'center',
                    gap: '4px',
                    border: `1px solid ${s.color}88`,
                    position: 'relative',
                  }}>
                    <s.icon size={20} color="rgba(255,255,255,0.9)" />
                    <span style={{ fontSize: '0.75rem', fontWeight: 900, color: 'rgba(255,255,255,0.9)', letterSpacing: '0.05em' }}>
                      {String(pathSteps.length - i).padStart(2, '0')}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: vertical alternating timeline — scroll-progress-driven */}
            <PathTimeline pathSteps={pathSteps} />

          </div>

          {/* CTA */}
          <div style={{ textAlign: 'center', marginTop: '5rem' }}>
            <button onClick={onAuthClick} className="cta-primary" style={{ fontSize: '1rem', padding: '1rem 2.5rem' }}>
              Begin Your Journey <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </section>


      <section id="team" style={{ paddingTop: '5rem', paddingBottom: '5rem', background: 'linear-gradient(160deg, #ffffff 0%, #f7f5ff 100%)' }}>
        <div className="container">
          <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
            <div className="section-label-pill">
              <span className="section-label-dot" />
              <span className="section-label-text">Our Team</span>
            </div>
            <h2 style={{ fontSize: 'clamp(2rem, 4vw, 3.5rem)', fontWeight: 900, color: 'var(--navy-dark)', marginBottom: '1rem' }}>The People Behind<br /><span className="text-gold-gradient">Ekalavya</span></h2>
          </div>
          <div className="team-marquee-wrapper">
            <div className="team-marquee-content">
              {/* Render twice for seamless infinite scrolling */}
              {[...teamMembers, ...teamMembers].map(({ name, role, img, badge }, idx) => (
                <div key={`${name}-${idx}`} className="glass-card" style={{ flex: '0 0 260px', padding: '1.75rem', textAlign: 'center', transition: 'transform 0.3s, box-shadow 0.3s', background: '#ffffff', border: '1px solid rgba(147,51,234,0.12)', boxShadow: '0 4px 20px rgba(147,51,234,0.03)' }} onMouseOver={e => { e.currentTarget.style.transform = 'translateY(-4px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(147,51,234,0.1)'; }} onMouseOut={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 4px 20px rgba(147,51,234,0.03)'; }}>
                  <div style={{ position: 'relative', display: 'inline-block', marginBottom: '1rem' }}>
                    <img src={img} alt={name} style={{ width: '5rem', height: '5rem', borderRadius: '50%', border: '2px solid rgba(147,51,234,0.2)', objectFit: 'cover' }} />
                    <div style={{ position: 'absolute', bottom: '-8px', right: '-8px', background: 'linear-gradient(135deg,var(--primary-gold),var(--primary-gold-deep))', color: 'white', fontSize: '0.6rem', fontWeight: 700, padding: '0.25rem 0.5rem', borderRadius: '9999px', textTransform: 'uppercase', whiteSpace: 'nowrap' }}>{badge}</div>
                  </div>
                  <h4 style={{ color: 'var(--navy-dark)', fontWeight: 700, marginBottom: '0.25rem' }}>{name}</h4>
                  <p style={{ color: 'var(--text-slate)', fontSize: '0.8rem' }}>{role}</p>
                </div>
              ))}
            </div>
          </div>
          <div style={{ textAlign: 'center', marginTop: '3rem' }}>
            <Link to="/team" className="cta-outline" style={{ display: 'inline-flex', alignItems: 'center', gap: '0.5rem', fontSize: '0.95rem', padding: '0.75rem 2rem', borderColor: 'var(--primary-gold)', color: 'var(--primary-gold-deep)', textDecoration: 'none' }}>
              Meet the Full Team <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* FAQ — CloudNestor-Inspired Two-Column Layout */}
      <section id="faq" style={{ paddingTop: '6rem', paddingBottom: '6rem', background: '#f8f7ff', position: 'relative', overflow: 'hidden' }}>
        {/* Subtle grid overlay */}
        <div style={{
          position: 'absolute', inset: 0, pointerEvents: 'none',
          backgroundImage: 'radial-gradient(rgba(147, 51, 234, 0.05) 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }} />
        {/* Purple ambient glow */}
        <div style={{ position: 'absolute', top: '20%', right: '-5%', width: '500px', height: '500px', background: 'radial-gradient(circle, rgba(147,51,234,0.07) 0%, transparent 70%)', filter: 'blur(60px)', pointerEvents: 'none' }} />

        <div className="container faq-cn-grid">
          {/* Left sticky column */}
          <div className="faq-cn-left">
            <span className="faq-cn-badge">FAQ</span>
            <h2 className="faq-cn-heading">
              Frequently<br />Asked<br /><span className="text-gold-gradient">Questions</span>
            </h2>
            <p className="faq-cn-sub">Everything you need to know about Ekalavya — answered clearly.</p>
          </div>

          {/* Right accordion column */}
          <div className="faq-cn-right">
            {faqs.map(({ q, a }, i) => (
              <FaqItem key={i} idx={i} q={q} a={a} open={openFaq === i} onToggle={() => setOpenFaq(openFaq === i ? null : i)} />
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
